import { getPostStatus, phaseOngoing, phaseComing, isParticipatoryBudget, isStudy } from '../Post';
import { PostStatuses, PostTypes, SortingFields, Steps, AllSteps, PostCategories } from '../Types';
import { StepType, SteppablePostType } from '../Types/Steps';
import { moment } from '../../I18n';

export const getStepName = (
  postType: SteppablePostType,
  stepType: any,
  openToCitizenAndContributor: any,
  intl: any,
) => {
  const isSubmissionStep = AllSteps.SUBMISSION === stepType;

  const stepTypeI18nKey =
    isSubmissionStep && !openToCitizenAndContributor ? 'CONTRIBUTION' : stepType.toUpperCase();

  const studyOrDefault = isStudy(postType) ? 'STUDY' : 'DEFAULT';

  return intl.formatMessage({
    id: `POST_STEPS_TYPES.${studyOrDefault}.${stepTypeI18nKey}`,
  });
};

export const getPostStepsWithStatus = (post: any, intl: any) => {
  const postType = post.type as SteppablePostType;
  if (!post.included.steps) return [];
  const steps = post.included.steps.map((step: any) => {
    // we override the step status if it's an ongoing Results step to make it "Available"
    let dateStatus = step.dates_status;
    if (step.step_type === Steps[postType].IMPLEMENTATION && dateStatus === 'ongoing') {
      dateStatus = 'available';
    }
    return {
      ...step,
      dates_status: dateStatus,
      label: getStepName(postType, step.step_type, step.open_to_citizen_and_contributor, intl),
      stateLabel: intl.formatMessage({
        id: `POSTS.PHASES.STATES.${dateStatus.toUpperCase()}`,
      }),
    };
  });
  return steps.filter((step: any) => step);
};

export const getPostStep = (post: any, intl: any) => {
  const postType = post.type as SteppablePostType;
  const postState = intl.formatMessage({
    id: `POSTS.PHASES.STATES.${getPostStatus(post).toUpperCase()}`,
  });

  if (PostCategories.COMMUNICATION.includes(postType)) {
    return null;
  }

  if (
    [
      PostTypes.PARTICIPATORY_BUDGET,
      PostTypes.COLLABORATIVE_DECISION,
      PostTypes.IDEAS_REQUEST,
      PostTypes.STUDY,
    ].includes(postType)
  ) {
    return (
      getPostStepsWithStatus(post, intl).find((step: any) => step.id === post.current_step_id)
        ?.label || postState
    );
  }

  return postState;
};

export const getDefaultStepIndex = (post: any) => {
  const ongoingStepIndex = (post.included.steps || []).findIndex(
    (step: any) => step.id === post.current_step_id,
  );

  const currentStep = (post.included.steps || []).find(
    (step: any) => step.id === post.current_step_id,
  );

  // TODO: should user the post.secured_strategies_contribute_resticted here ? but it wont work for studies
  if (ongoingStepIndex > -1 && currentStep.open_to_citizen_and_contributor) return ongoingStepIndex;

  return 0;
};

export const currentStep = (post: any) =>
  (post.included.steps || []).find((step: any) => step.id === post.current_step_id);

export const getStatusFilterForStep = (postType: SteppablePostType, activeStep: any) => {
  if (activeStep?.step_type === Steps[postType].VOTE) {
    return [
      PostStatuses.SELECTED,
      PostStatuses.VALIDATED,
      PostStatuses.IN_PROGRESS,
      PostStatuses.DONE,
    ];
  }

  if (activeStep.step_type === Steps[postType].IMPLEMENTATION) {
    return [PostStatuses.VALIDATED, PostStatuses.IN_PROGRESS, PostStatuses.DONE];
  }

  return [
    PostStatuses.TODO,
    PostStatuses.DOING,
    PostStatuses.SELECTED,
    PostStatuses.ANSWERED,
    PostStatuses.VALIDATED,
    PostStatuses.DUPLICATE,
    PostStatuses.IN_PROGRESS,
    PostStatuses.DONE,
  ];
};

export const getAvailableSortOptionsForStep = (postType: SteppablePostType, activeStep: any) => {
  if (!activeStep?.step_type) return [];

  const voteSortingOptions = [SortingFields.RANDOM, SortingFields.UPVOTED, SortingFields.COMMENTED];

  if (isParticipatoryBudget(postType)) {
    voteSortingOptions.push(SortingFields.LEAST_EXPENSIVE);
  }

  switch (activeStep.step_type) {
    case Steps[postType].SUBMISSION:
      if (postType === PostTypes.IDEAS_REQUEST) {
        return [SortingFields.RECENT, SortingFields.UPVOTED, SortingFields.COMMENTED];
      }
      return [SortingFields.RANDOM, SortingFields.RECENT, SortingFields.COMMENTED];
    case Steps[postType].ANALYSIS:
      return [SortingFields.RANDOM, SortingFields.RECENT, SortingFields.COMMENTED];
    case Steps[postType].VOTE:
      return voteSortingOptions;
    case Steps[postType].IMPLEMENTATION:
      return [SortingFields.RANDOM, SortingFields.RECENT, SortingFields.COMMENTED];

    default:
      return [];
  }
};

export const isVoteStepOngoing = (steps: Array<any> = [], postType: SteppablePostType) => {
  if (!steps.filter((step: any) => step)?.length || !postType) return false;

  const voteStepOngoing = phaseOngoing(
    steps.find((step: any) => step.step_type === Steps[postType].VOTE)?.dates_status,
  );

  const submissionOnIdeasRequestOngoing =
    postType === PostTypes.IDEAS_REQUEST &&
    phaseOngoing(steps.find((step: any) => step.step_type === Steps[postType])?.dates_status);

  return voteStepOngoing || submissionOnIdeasRequestOngoing;
};

export const isSubmissionStepOngoing = (steps: Array<any> = [], postType: SteppablePostType) => {
  if (!steps.length || !postType) return false;

  return steps.some(
    (step: any) => step.step_type === Steps[postType].SUBMISSION && phaseOngoing(step.dates_status),
  );
};

export const voteDisabled = (post: any) => {
  return post.status === PostStatuses.ANSWERED;
};

export const getLastStepEndDate = (post: any) => {
  const steps = post.included.steps || [];

  // return the latest end_at of all the steps
  return steps.reduce(
    (prev: any, curr: any) => (moment(curr.end_at).isAfter(moment(prev.end_at)) ? curr : prev),
    steps[0],
  )?.end_at;
};

export const allStepsEnded = (post: any) => {
  const lastEndAt = getLastStepEndDate(post);

  return lastEndAt && moment(lastEndAt).isBefore(moment());
};

export const allStepsComing = (post: any) => {
  const steps = post.included?.steps || [];

  return steps.every((step: any) => phaseComing(step.dates_status));
};

export const restricteableStep = (postType: SteppablePostType, stepType: StepType) => {
  if (postType === PostTypes.STUDY && stepType === Steps[postType].IMPLEMENTATION) {
    return true;
  }

  return false;
};

export const moderatableStep = (postType: SteppablePostType, stepType: StepType) => {
  if (
    [
      PostTypes.COLLABORATIVE_DECISION,
      PostTypes.PARTICIPATORY_BUDGET,
      PostTypes.IDEAS_REQUEST,
    ].includes(postType) &&
    stepType === Steps[postType].SUBMISSION
  ) {
    return true;
  }

  return false;
};

export const daysLeft = (step: any) => {
  const endDate = moment(step.end_at);
  const todaysDate = moment();

  return endDate.diff(todaysDate, 'days');
};

export const isImplementationStep = (stepType: any) => {
  return stepType === AllSteps.IMPLEMENTATION;
};

const stepIndex = (post: any, stepType: StepType) => {
  const steps = post.included?.steps || [];

  return steps.findIndex((s: any) => s.step_type === stepType);
};

const rootAttributePath = (post: any, stepType: StepType) =>
  `steps_attributes[${stepIndex(post, stepType)}]`;
export const attributePath = (post: any, stepType: StepType, name: string) =>
  `${rootAttributePath(post, stepType)}.${name}`;

const rootErrorPath = (post: any, stepType: StepType) => `steps[${stepIndex(post, stepType)}]`;
export const errorPath = (post: any, stepType: StepType, name: string) =>
  `${rootErrorPath(post, stepType)}.${name}`;
